import type { MigrationKind, MigrationPlanDTO } from '@devrobot/shared';
import { ArrowRight, CircleAlert, CircleCheck, CircleMinus, Database, FileText, HardDrive, KeyRound, Route, SlidersHorizontal, TriangleAlert } from 'lucide-react';
import { formatBytes, pluralize } from '../../lib/format';

export const KIND_BADGE: Record<MigrationKind, { label: string; cls: string }> = {
  'server-move': { label: 'server move', cls: 'badge-soft badge-info' },
  'environment-copy': { label: 'environment copy', cls: 'badge-soft badge-secondary' },
};

export function KindBadge({ kind, size = 'sm' }: { kind: MigrationKind; size?: 'xs' | 'sm' | 'md' }) {
  const k = KIND_BADGE[kind];
  const sz = size === 'xs' ? 'badge-xs' : size === 'md' ? 'badge-md' : 'badge-sm';
  return <span className={`badge ${sz} whitespace-nowrap ${k.cls}`}>{k.label}</span>;
}

export const DOWNTIME_TEXT: Record<MigrationPlanDTO['estimatedDowntime'], string> = {
  none: 'No downtime expected: the source keeps serving while data is copied.',
  'volume-owners': 'Services that use the copied databases or volumes stop while their data is copied, then start again.',
  full: 'The whole source application is stopped while data is copied, then started again.',
};

const CREDENTIAL_TEXT = { 'copy-source': 'copy the source credential', 'keep-destination': 'keep the destination credential', generate: 'generate a new credential' } as const;
const ROUTE_TEXT = { 'generate-new': 'a new generated hostname', 'move-at-cutover': 'moves to the new VPS at cutover', 'not-routed': 'not routed at the destination' } as const;

/** "env on vps" for one side of a migration. */
export function Where({ environment, vps }: { environment: string; vps: { name: string } }) {
  return (
    <span className="break-anywhere">
      <span className="font-medium">{environment}</span>
      <span className="text-base-content/55"> on </span>
      <span>{vps.name}</span>
    </span>
  );
}

export function SourceToDestination({ source, destination }: { source: { environment: string; vps: { name: string } }; destination: { environment: string; vps: { name: string } } }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
      <Where {...source} />
      <ArrowRight className="size-3.5 shrink-0 text-base-content/50" aria-label="to" />
      <Where {...destination} />
    </span>
  );
}

function Count({ icon: Icon, label, value, detail }: { icon: typeof Database; label: string; value: number | string; detail?: string }) {
  return (
    <div className="min-w-0 rounded-field bg-base-200/60 p-3">
      <dt className="flex items-center gap-1.5 text-xs text-base-content/55">
        <Icon className="size-3.5" aria-hidden="true" /> {label}
      </dt>
      <dd className="mt-1 text-sm font-semibold tabular-nums">{value}</dd>
      {detail ? <dd className="truncate text-xs text-base-content/55" title={detail}>{detail}</dd> : null}
    </div>
  );
}

function Messages({ items, tone, title }: { items: string[]; tone: 'error' | 'warning'; title: string }) {
  if (items.length === 0) return null;
  const Icon = tone === 'error' ? CircleAlert : TriangleAlert;
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={`alert ${tone === 'error' ? 'alert-error' : 'alert-warning'} alert-soft items-start text-sm`}>
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0">
        <p className="font-semibold">{title}</p>
        <ul className="list-disc pl-5">
          {items.map((m, i) => (
            <li key={i} className="break-anywhere">
              {m}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * What a migration will do, as computed by the server: steps (skipped ones greyed), data and
 * configuration counts, credential and route handling, downtime, blockers and warnings.
 * `selection` narrows the counts to what the request includes (the plan lists all candidates).
 */
export function PlanSummary({
  plan,
  selection,
  showMessages = true,
}: {
  plan: MigrationPlanDTO;
  selection?: { databases: string[]; volumes: string[]; variables: string[]; files: string[] };
  showMessages?: boolean;
}) {
  const dbs = selection ? plan.databases.filter((d) => selection.databases.includes(d.service)) : plan.databases;
  const vols = selection ? plan.volumes.filter((v) => selection.volumes.includes(v.name)) : plan.volumes;
  const vars = selection ? plan.config.variables.filter((v) => selection.variables.includes(v.name)) : plan.config.variables;
  const files = selection ? plan.config.files.filter((f) => selection.files.includes(f.name)) : plan.config.files;
  const secrets = vars.filter((v) => v.secret).length;
  const disk = plan.disk;

  return (
    <div className="space-y-5">
      {showMessages ? (
        <>
          <Messages items={plan.blockers} tone="error" title="Blocked: the migration cannot start" />
          <Messages items={plan.warnings} tone="warning" title="Warnings" />
        </>
      ) : null}

      <dl className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Count icon={Database} label="Databases" value={dbs.length} detail={dbs.map((d) => d.service).join(', ') || undefined} />
        <Count icon={HardDrive} label="Volumes" value={vols.length} detail={vols.map((v) => v.name).join(', ') || undefined} />
        <Count icon={SlidersHorizontal} label="Variables" value={vars.length} detail={secrets ? `${pluralize(secrets, 'secret')}` : undefined} />
        <Count icon={FileText} label="Files" value={files.length} />
      </dl>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-field bg-base-200/60 p-3 text-sm">
          <p className="text-xs font-medium tracking-wide text-base-content/55 uppercase">Downtime</p>
          <p className="mt-1">{DOWNTIME_TEXT[plan.estimatedDowntime]}</p>
        </div>
        <div className="rounded-field bg-base-200/60 p-3 text-sm">
          <p className="text-xs font-medium tracking-wide text-base-content/55 uppercase">Disk</p>
          <p className="mt-1 tabular-nums">
            needs {disk.requiredBytes === null ? 'unknown' : formatBytes(disk.requiredBytes)} · destination free {disk.destinationFreeBytes === null ? 'unknown' : formatBytes(disk.destinationFreeBytes)}
          </p>
          {disk.requiredBytes !== null && disk.destinationFreeBytes !== null && disk.requiredBytes > disk.destinationFreeBytes ? (
            <p className="dr-ink-error">The destination VPS does not have enough free disk.</p>
          ) : null}
        </div>
      </div>

      {plan.credentials.length > 0 ? (
        <div>
          <h3 className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold">
            <KeyRound className="size-4" aria-hidden="true" /> Database credentials
          </h3>
          <ul className="space-y-1 text-sm">
            {plan.credentials.map((c) => (
              <li key={`${c.database}-${c.variable}`} className="break-anywhere">
                <code className="font-mono">{c.variable}</code> <span className="text-base-content/60">({c.database})</span>: {CREDENTIAL_TEXT[c.action]}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {plan.routes.length > 0 ? (
        <div>
          <h3 className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold">
            <Route className="size-4" aria-hidden="true" /> Hostnames
          </h3>
          <ul className="space-y-1 text-sm">
            {plan.routes.map((r) => (
              <li key={r.hostname} className="break-anywhere">
                <span className="font-mono">{r.hostname}</span>: {ROUTE_TEXT[r.action]}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div>
        <h3 className="mb-1.5 text-sm font-semibold">Steps</h3>
        <ol className="space-y-1.5" aria-label="Migration steps">
          {plan.steps.map((st, i) => (
            <li key={st.name} className={`flex items-start gap-2 text-sm ${st.skipped ? 'text-base-content/45' : ''}`}>
              {st.skipped ? <CircleMinus className="mt-0.5 size-4 shrink-0" aria-hidden="true" /> : <CircleCheck className="mt-0.5 size-4 shrink-0 dr-ink-success" aria-hidden="true" />}
              <span className="min-w-0">
                <span className="font-mono text-xs">
                  {i + 1}. {st.name}
                </span>
                {st.skipped ? <span className="badge badge-ghost badge-xs ml-1.5">skipped</span> : null}
                <span className="block">{st.description}</span>
              </span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
